import React, { useRef, useEffect } from "react";
import {
  Autocomplete,
  Avatar,
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
// import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useUserListMessaging } from "./useUserListMessaging";
import { searchUsers } from "./messagingService";

const ROLE_COLORS = {
  "Game-Owner": "#0ea5e9",
  "Master-Distributor": "#a855f7",
  Distributor: "#ef4444",
  "Sub-Distributor": "#f59e0b",
  Agent: "#6b7280",
  Player: "#16a34a",
};

const formatConversationTime = (value) => {
  if (!value) return "";

  return new Date(value).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const formatMessageStamp = (value) => {
  if (!value) return "";

  return new Date(value).toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const formatMessageDate = (value) => {
  if (!value) return "";

  return new Date(value).toLocaleDateString("en-GB");
};

const initialsFromUsername = (username = "") =>
  username
    .split(/[\s_.-]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "U";

const RolePill = ({ role }) => (
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      px: 0.9,
      py: 0.22,
      borderRadius: 999,
      border: `1px solid ${ROLE_COLORS[role] || "#4b5563"}`,
      color: ROLE_COLORS[role] || "#d1d5db",
      fontSize: "0.58rem",
      lineHeight: 1.15,
      whiteSpace: "nowrap",
    }}
  >
    {role}
  </Box>
);

const HeaderUnreadBadge = ({ totalUnread }) =>
  totalUnread > 0 ? (
    <Box
      sx={{
        minWidth: 20,
        height: 20,
        px: 0.75,
        borderRadius: "50%",
        backgroundColor: "#ff1f1f",
        color: "#fff",
        fontSize: "0.72rem",
        fontWeight: 700,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        lineHeight: 1,
        flexShrink: 0,
      }}
    >
      {totalUnread}
    </Box>
  ) : null;

const MessagingPanel = () => {
  const {
    open,
    setOpen,
    chatUsers,
    selectedUser,
    messages,
    text,
    setText,
    search,
    setSearch,
    roleFilter,
    setRoleFilter,
    fromDate,
    setFromDate,
    toDate,
    setToDate,
    selectUser,
    send,
    totalUnread,
    clearFilters,
    loadingUsers,
    loadingMessages,
    searching,
    sending,
    roleOptions,
    sendDirectMessage,
    messagingEnabled,
  } = useUserListMessaging();

  const endRef = useRef(null);
  const composeSearchTimerRef = useRef(null);
  const [composeOpen, setComposeOpen] = React.useState(false);
  const [composeRole, setComposeRole] = React.useState("");
  const [composeUser, setComposeUser] = React.useState(null);
  const [composeText, setComposeText] = React.useState("");
  const [composeSearch, setComposeSearch] = React.useState("");
  const [composeUsers, setComposeUsers] = React.useState([]);
  const [composeLoading, setComposeLoading] = React.useState(false);
  const [composeSending, setComposeSending] = React.useState(false);

  useEffect(() => {
    if (endRef.current) {
      const parent = endRef.current.parentElement;
      if (parent) {
        parent.scrollTo({
          top: parent.scrollHeight,
          behavior: "smooth",
        });
      }
    }
  }, [messages]);

  useEffect(() => {
    clearTimeout(composeSearchTimerRef.current);

    if (!composeOpen || !composeRole) {
      setComposeUsers([]);
      setComposeLoading(false);
      return;
    }

    let mounted = true;

    composeSearchTimerRef.current = setTimeout(async () => {
      try {
        setComposeLoading(true);
        const users = await searchUsers(composeSearch, composeRole);
        if (mounted) {
          setComposeUsers(users || []);
        }
      } catch (error) {
        console.error("Failed to load users for new message", error);
      } finally {
        if (mounted) {
          setComposeLoading(false);
        }
      }
    }, 300);

    return () => {
      mounted = false;
      clearTimeout(composeSearchTimerRef.current);
    };
  }, [composeOpen, composeRole, composeSearch]);

  const closeComposeDialog = () => {
    setComposeOpen(false);
    setComposeRole("");
    setComposeUser(null);
    setComposeText("");
    setComposeSearch("");
    setComposeUsers([]);
  };

  const handleComposeSend = async () => {
    if (!composeUser || !composeText.trim()) return;

    try {
      setComposeSending(true);
      await sendDirectMessage(composeUser, composeText);
      closeComposeDialog();
    } catch (error) {
      console.error("Failed to send new message", error);
    } finally {
      setComposeSending(false);
    }
  };

  if (!messagingEnabled) {
    return null;
  }

  if (!open) {
    return (
      <Box
        onClick={() => setOpen(true)}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          minHeight: 44,
          px: 2,
          py: 1,
          border: "1px solid #d4a017",
          borderRadius: 1,
          backgroundColor: "#242424",
          cursor: "pointer",
          "&:hover": { backgroundColor: "#2b2b2b" },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <ChatBubbleOutlineIcon sx={{ fontSize: 18, color: "#f3f4f6" }} />
          <Typography sx={{ color: "#f9fafb", fontSize: "0.95rem" }}>
            All messages
          </Typography>
          <HeaderUnreadBadge totalUnread={totalUnread} />
        </Box>

        <KeyboardArrowDownIcon sx={{ color: "#e5e7eb", fontSize: 20 }} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        mt: 1,
        border: "1px solid rgba(212,160,23,0.85)",
        borderRadius: 1,
        backgroundColor: "#242424",
        overflow: "hidden",
        height: { xs: 520, md: 620 },
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          px: 2,
          py: 1.2,
          borderBottom: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
          <ChatBubbleOutlineIcon sx={{ fontSize: 18, color: "#f9fafb" }} />
          <Typography sx={{ color: "#f9fafb", fontSize: "0.95rem" }}>
            All messages
          </Typography>
          <HeaderUnreadBadge totalUnread={totalUnread} />
        </Box>

        <IconButton
          size="small"
          onClick={() => setOpen(false)}
          sx={{ color: "#f3f4f6" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "33% 67%" },
          flex: 1,
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            borderRight: { xs: "none", md: "1px solid rgba(255,255,255,0.08)" },
            borderBottom: { xs: "1px solid rgba(255,255,255,0.08)", md: "none" },
            backgroundColor: "#222222",
            display: "flex",
            flexDirection: "column",
            minHeight: 0,
          }}
        >
          <Box sx={{ p: 1.5, display: "flex", gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search Username"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              sx={{
                "& .MuiOutlinedInput-root": {
                  backgroundColor: "#2a2a2a",
                  color: "#fff",
                  fontSize: "0.85rem",
                  "& fieldset": { borderColor: "rgba(255,255,255,0.08)" },
                  "&:hover fieldset": { borderColor: "rgba(255,255,255,0.16)" },
                  "&.Mui-focused fieldset": { borderColor: "#d4a017" },
                },
                "& .MuiInputBase-input::placeholder": {
                  color: "#9ca3af",
                  opacity: 1,
                },
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#9ca3af", fontSize: 18 }} />
                  </InputAdornment>
                ),
                endAdornment: searching ? (
                  <InputAdornment position="end">
                    <CircularProgress size={16} sx={{ color: "#d4a017" }} />
                  </InputAdornment>
                ) : null,
              }}
            />
            <IconButton
              onClick={() => setComposeOpen(true)}
              size="small"
              sx={{
                width: 36,
                height: 36,
                borderRadius: 1,
                backgroundColor: "#f7b500",
                color: "#111827",
                "&:hover": { backgroundColor: "#e2a600" },
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ px: 1.5, pb: 1.5, display: "flex", gap: 1 }}>
            <TextField
              fullWidth
              size="small"
              type="date"
              label="From Date"
              value={fromDate}
              onChange={(event) => setFromDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={dateFieldSx}
            />
            <TextField
              fullWidth
              size="small"
              type="date"
              label="To Date"
              value={toDate}
              onChange={(event) => setToDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              sx={dateFieldSx}
            />
            <Select
              size="small"
              displayEmpty
              value={roleFilter}
              onChange={(event) => setRoleFilter(event.target.value)}
              sx={{
                minWidth: 110,
                backgroundColor: "#2a2a2a",
                color: "#fff",
                fontSize: "0.78rem",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(255,255,255,0.08)",
                },
                "&:hover .MuiOutlinedInput-notchedOutline": {
                  borderColor: "rgba(255,255,255,0.16)",
                },
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#d4a017",
                },
                "& .MuiSelect-icon": { color: "#d1d5db" },
              }}
              renderValue={(value) => value || "All Users"}
            >
              <MenuItem value="">All Users</MenuItem>
              {roleOptions.map((role) => (
                <MenuItem key={role} value={role}>
                  {role}
                </MenuItem>
              ))}
            </Select>
          </Box>

          {(search || roleFilter || fromDate || toDate) && (
            <Box sx={{ px: 1.5, pb: 1.5 }}>
              <Typography
                onClick={clearFilters}
                sx={{
                  color: "#d4a017",
                  fontSize: "0.78rem",
                  cursor: "pointer",
                  width: "fit-content",
                }}
              >
                Clear filters
              </Typography>
            </Box>
          )}

          <Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />

          <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
            {loadingUsers ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress size={26} sx={{ color: "#d4a017" }} />
              </Box>
            ) : chatUsers.length === 0 ? (
              <Typography sx={{ px: 2, py: 4, color: "#9ca3af", fontSize: "0.85rem" }}>
                No conversations found.
              </Typography>
            ) : (
              chatUsers.map((user) => {
                const isActive =
                  (selectedUser?.userId || selectedUser?.id) ===
                  (user.userId || user.id);

                return (
                  <Box
                    key={user.userId || user.id}
                    onClick={() => selectUser(user)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1.1,
                      py: 1.15,
                      cursor: "pointer",
                      borderBottom: "1px solid rgba(255,255,255,0.06)",
                      backgroundColor: isActive ? "#2a2a2a" : "transparent",
                      "&:hover": { backgroundColor: "#2a2a2a" },
                    }}
                  >
                    <Avatar
                      sx={{
                        width: 20,
                        height: 20,
                        fontSize: "0.5rem",
                        backgroundColor: "#4f4f4f",
                        color: "#d1d5db",
                        flexShrink: 0,
                      }}
                    >
                      {initialsFromUsername(user.username)}
                    </Avatar>

                    <Box
                      sx={{
                        minWidth: 0,
                        flex: 1,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: 1,
                      }}
                    >
                      <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                          sx={{
                            color: "#f3f4f6",
                            fontSize: "0.84rem",
                            lineHeight: 1.15,
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                        >
                          {user.username}
                        </Typography>

                        <Box
                          sx={{
                            mt: 0.55,
                            display: "flex",
                            alignItems: "center",
                            gap: 0.55,
                            minWidth: 0,
                          }}
                        >
                          <RolePill role={user.role} />
                          {user.unread > 0 && (
                            <Badge
                              badgeContent={user.unread}
                              color="error"
                              sx={{
                                "& .MuiBadge-badge": {
                                  position: "static",
                                  transform: "none",
                                  minWidth: 16,
                                  height: 16,
                                  px: 0.45,
                                  fontSize: "0.56rem",
                                  fontWeight: 700,
                                },
                              }}
                            />
                          )}
                        </Box>
                      </Box>

                      <Typography
                        sx={{
                          minWidth: 58,
                          color: "#f3f4f6",
                          fontSize: "0.72rem",
                          lineHeight: 1,
                          textAlign: "right",
                          whiteSpace: "nowrap",
                          flexShrink: 0,
                        }}
                      >
                        {formatConversationTime(user.lastMessageAt)}
                      </Typography>
                    </Box>
                  </Box>
                );
              })
            )}
          </Box>
        </Box>

        <Box sx={{ display: "flex", flexDirection: "column", minHeight: 0 }}>
          {selectedUser ? (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  px: 2,
                  py: 1.4,
                  borderBottom: "1px solid rgba(255,255,255,0.08)",
                  backgroundColor: "#252525",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
                  <Avatar
                    sx={{
                      width: 28,
                      height: 28,
                      fontSize: "0.72rem",
                      backgroundColor: "#4b5563",
                    }}
                  >
                    {initialsFromUsername(selectedUser.username)}
                  </Avatar>
                  <Box>
                    <Typography sx={{ color: "#f9fafb", fontSize: "0.98rem" }}>
                      {selectedUser.username}
                    </Typography>
                    <Box sx={{ }}>
                      <RolePill role={selectedUser.role} />
                    </Box>
                  </Box>
                </Box>

                <IconButton size="small" sx={{ color: "#9ca3af" }}>
                  <MoreVertIcon fontSize="small" />
                </IconButton>
              </Box>

              <Box
                sx={{
                  flex: 1,
                  minHeight: 0,
                  px: 2,
                  py: 1.5,
                  backgroundColor: "#262626",
                  overflowY: "auto",
                }}
              >
                {loadingMessages ? (
                  <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                    <CircularProgress size={28} sx={{ color: "#d4a017" }} />
                  </Box>
                ) : messages.length === 0 ? (
                  <Typography sx={{ color: "#9ca3af", fontSize: "0.86rem", py: 4 }}>
                    No messages yet
                  </Typography>
                ) : (
                  messages.map((message, index) => {
                    const previousMessage = messages[index - 1];
                    const showDateLabel =
                      !previousMessage ||
                      formatMessageDate(previousMessage.createdAt) !==
                        formatMessageDate(message.createdAt);

                    return (
                      <Box key={message.id || `${message.senderId}-${index}`} sx={{ mb: 1.5 }}>
                        {showDateLabel && (
                          <Box sx={{ display: "flex", justifyContent: "center", mb: 1.5 }}>
                            <Typography
                              sx={{
                                px: 1,
                                py: 0.25,
                                borderRadius: 0.5,
                                backgroundColor: "rgba(255,255,255,0.08)",
                                color: "#d1d5db",
                                fontSize: "0.62rem",
                              }}
                            >
                              {formatMessageDate(message.createdAt)}
                            </Typography>
                          </Box>
                        )}

                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: message.isMe ? "flex-end" : "flex-start",
                          }}
                        >
                          <Box
                            sx={{
                              maxWidth: "62%",
                              px: 1.5,
                              py: 1.2,
                              borderRadius: 2,
                              backgroundColor: message.isMe ? "#4a4a4a" : "#353535",
                              color: "#fff",
                            }}
                          >
                            <Typography sx={{ fontSize: "0.88rem", lineHeight: 1.5 }}>
                              {message.text}
                            </Typography>
                            <Typography
                              sx={{
                                mt: 0.5,
                                textAlign: "right",
                                color: "#9ca3af",
                                fontSize: "0.62rem",
                              }}
                            >
                              {formatMessageStamp(message.createdAt)}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    );
                  })
                )}
                <div ref={endRef} />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  p: 1.5,
                  borderTop: "1px solid rgba(255,255,255,0.08)",
                  backgroundColor: "#242424",
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Write your message ..."
                  value={text}
                  onChange={(event) => {
                    if (event.target.value.length <= 500) {
                      setText(event.target.value);
                    }
                  }}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" && !event.shiftKey) {
                      event.preventDefault();
                      send();
                    }
                  }}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      backgroundColor: "#2d2d2d",
                      color: "#fff",
                      borderRadius: 1,
                      "& fieldset": { borderColor: "transparent" },
                      "&:hover fieldset": { borderColor: "transparent" },
                      "&.Mui-focused fieldset": { borderColor: "#d4a017" },
                    },
                    "& .MuiInputBase-input::placeholder": {
                      color: "#8a8f98",
                      opacity: 1,
                    },
                  }}
                />

                <IconButton
                  onClick={send}
                  disabled={!text.trim() || sending}
                  sx={{
                    width: 38,
                    height: 38,
                    backgroundColor: "#f7b500",
                    color: "#111827",
                    "&:hover": { backgroundColor: "#e2a600" },
                    "&.Mui-disabled": {
                      backgroundColor: "#5b5b5b",
                      color: "#c7c7c7",
                    },
                  }}
                >
                  {sending ? (
                    <CircularProgress size={18} sx={{ color: "#111827" }} />
                  ) : (
                    <SendIcon fontSize="small" />
                  )}
                </IconButton>
              </Box>
            </>
          ) : (
            <Box
              sx={{
                flex: 1,
                minHeight: 0,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#9ca3af",
                backgroundColor: "#262626",
                px: 3,
                textAlign: "center",
              }}
            >
              Select a conversation from the left to view messages.
            </Box>
          )}
        </Box>
      </Box>

      <Dialog
        open={composeOpen}
        onClose={closeComposeDialog}
        fullWidth
        maxWidth="sm"
        PaperProps={{
          sx: {
            backgroundColor: "#3b3b3b",
            color: "#fff",
            borderRadius: 2,
            overflow: "hidden",
          },
        }}
      >
        <DialogTitle
          sx={{
            position: "relative",
            fontSize: "1.15rem",
            fontWeight: 500,
            color: "#f3f4f6",
            px: 2.5,
            py: 2,
            borderBottom: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          New Message
          <IconButton
            onClick={closeComposeDialog}
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              color: "#94a3b8",
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ px: 2.5, py: 2.5 }}>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box>
              <Typography sx={composeLabelSx}>Select Role</Typography>
              <Select
                fullWidth
                displayEmpty
                value={composeRole}
                onChange={(event) => {
                  setComposeRole(event.target.value);
                  setComposeUser(null);
                  setComposeSearch("");
                }}
                sx={composeInputSx}
                renderValue={(value) => value || "Select role"}
              >
                <MenuItem value="">Select role</MenuItem>
                {roleOptions.map((role) => (
                  <MenuItem key={role} value={role}>
                    {role}
                  </MenuItem>
                ))}
              </Select>
            </Box>

            <Box>
              <Typography sx={composeLabelSx}>Username</Typography>
              <Autocomplete
                options={composeUsers}
                loading={composeLoading}
                value={composeUser}
                inputValue={composeSearch}
                onInputChange={(_, value) => setComposeSearch(value)}
                onChange={(_, value) => setComposeUser(value)}
                getOptionLabel={(option) => option?.username || ""}
                noOptionsText={
                  !composeRole
                    ? "Select role first"
                    : "No usernames found for this role"
                }
                isOptionEqualToValue={(option, value) =>
                  (option.userId || option.id) === (value.userId || value.id)
                }
                disabled={!composeRole}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder={
                      composeRole
                        ? "Enter or select username"
                        : "Select role first"
                    }
                    sx={composeAutocompleteSx}
                  />
                )}
                ListboxProps={{
                  sx: {
                    backgroundColor: "#3b3b3b",
                    color: "#fff",
                    "& .MuiAutocomplete-option": {
                      alignItems: "center",
                      minHeight: 42,
                      borderBottom: "1px solid rgba(255,255,255,0.06)",
                    },
                    "& .MuiAutocomplete-option[aria-selected='true']": {
                      backgroundColor: "#474747",
                    },
                    "& .MuiAutocomplete-option.Mui-focused": {
                      backgroundColor: "#4a4a4a",
                    },
                  },
                }}
                slotProps={{
                  paper: {
                    sx: {
                      backgroundColor: "#3b3b3b",
                      color: "#fff",
                      border: "1px solid rgba(255,255,255,0.08)",
                      mt: 0.5,
                    },
                  },
                }}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography sx={{ color: "#f9fafb", fontSize: "0.92rem" }}>
                        {option.username}
                      </Typography>
                      <RolePill role={option.role} />
                    </Box>
                  </Box>
                )}
              />
            </Box>

            <Box>
              <Typography sx={composeLabelSx}>Message</Typography>
              <TextField
                fullWidth
                multiline
                minRows={5}
                value={composeText}
                onChange={(event) => {
                  if (event.target.value.length <= 500) {
                    setComposeText(event.target.value);
                  }
                }}
                placeholder="Write your message...."
                sx={composeTextareaSx}
              />
              <Typography sx={{ mt: 0.75, color: "#d1d5db", fontSize: "0.82rem" }}>
                {composeText.length}/500 characters
              </Typography>
            </Box>

            <Button
              fullWidth
              variant="contained"
              onClick={handleComposeSend}
              disabled={!composeUser || !composeText.trim() || composeSending}
              sx={{
                mt: 0.5,
                py: 1.2,
                backgroundColor: "#f7b500",
                color: "#111827",
                fontSize: "1rem",
                fontWeight: 500,
                textTransform: "none",
                boxShadow: "none",
                "&:hover": {
                  backgroundColor: "#e2a600",
                  boxShadow: "none",
                },
                "&.Mui-disabled": {
                  backgroundColor: "#756224",
                  color: "#222",
                },
              }}
            >
              {composeSending ? "Sending..." : "Send Message"}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

    </Box>
  );
};

const dateFieldSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#2a2a2a",
    color: "#fff",
    fontSize: "0.76rem",
    "& fieldset": { borderColor: "rgba(255,255,255,0.08)" },
    "&:hover fieldset": { borderColor: "rgba(255,255,255,0.16)" },
    "&.Mui-focused fieldset": { borderColor: "#d4a017" },
  },
  "& .MuiInputLabel-root": {
    color: "#9ca3af",
    fontSize: "0.72rem",
  },
  "& .MuiInputBase-input": {
    color: "#fff",
    py: 1,
  },
  "& input::-webkit-calendar-picker-indicator": {
    filter: "invert(1)",
  },
};

const composeLabelSx = {
  mb: 0.75,
  color: "#e5e7eb",
  fontSize: "0.98rem",
};

const composeInputSx = {
  backgroundColor: "#404040",
  color: "#fff",
  borderRadius: 1.5,
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(255,255,255,0.12)",
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: "rgba(255,255,255,0.22)",
  },
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#f7b500",
  },
  "& .MuiSelect-select": {
    py: 1.6,
    color: "#d1d5db",
  },
  "& .MuiSelect-icon": {
    color: "#9ca3af",
  },
};

const composeAutocompleteSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#404040",
    color: "#fff",
    borderRadius: 1.5,
    "& fieldset": {
      borderColor: "rgba(255,255,255,0.12)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(255,255,255,0.22)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#f7b500",
    },
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#9ca3af",
    opacity: 1,
  },
  "& .MuiAutocomplete-popupIndicator, & .MuiAutocomplete-clearIndicator": {
    color: "#9ca3af",
  },
};

const composeTextareaSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#404040",
    color: "#fff",
    borderRadius: 1.5,
    alignItems: "flex-start",
    "& fieldset": {
      borderColor: "rgba(255,255,255,0.12)",
    },
    "&:hover fieldset": {
      borderColor: "rgba(255,255,255,0.22)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#f7b500",
    },
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#9ca3af",
    opacity: 1,
  },
};

export default MessagingPanel;
